// @flow
import React, { PureComponent } from 'react';
import { withRouter, Link } from 'react-router-dom';

import PageNotFound from 'components/app/PageNotFound';
import Loading from 'components/fragments/Loading';
import JobDetailForm from 'components/job/jobDetail/JobDetailForm';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import { STATUS_LOADING, STATUS_DEFAULT, STATUS_UNKNOWN } from 'constants/statusConstants';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { JobDetailType } from 'types/JobDetail';
import type { BlocContent } from 'types/NewsletterBloc';
import type { RouterProps } from 'types/Router';
import type { Status } from 'types/Status';

export type StateProps = {
  title: string,
  description: string,
  details: JobDetailType,
  status: Status,
  sidebar_content: Array<BlocContent>,
  meta_title: string,
  meta_description: string,
  structureName: string,
  contactFormList: Array<any>
};

export type DispatchProps = {
  fetchContactFormList: () => void,
  fetchJobDetail: (slug: string) => void
};

type Props = DispatchProps & RouterProps & StateProps;

// https://www.codementor.io/blog/react-optimization-5wiwjnf9hj#2-functionstateless-components-and-reactpurecomponent
class JobDetail extends PureComponent<Props> {
  componentDidMount() {
    const {
      match: { params }
    } = this.props;
    this.props.fetchContactFormList();

    if (params && typeof params.jobSlug === 'string') {
      this.props.fetchJobDetail(params.jobSlug);
    }
  }

  componentDidUpdate(prevProps) {
    const slug = this.props.match.params ? this.props.match.params.jobSlug : '';
    const prevSlug = prevProps.match.params ? prevProps.match.params.jobSlug : '';

    if (typeof slug === 'string' && slug !== prevSlug) {
      this.props.fetchJobDetail(slug);
    }
  }

  renderDetail = () => {
    const { content } = this.props.details;
    return content.map((blocFigure, index) => {
      return <ContentBloc key={index} {...blocFigure} />;
    });
  };

  render() {
    const {
      match: { url },
      status,
      details
    } = this.props;
    if (status === STATUS_LOADING || status === STATUS_DEFAULT) {
      return <Loading />;
    }

    if (status === STATUS_UNKNOWN) {
      return <PageNotFound noMargin />;
    }

    const form = this.props.contactFormList.find(({ slug }) => slug === 'postuler') ?? -1;

    const backUrl = url
      .split('/')
      .slice(0, -2)
      .join('/');

    return (
      <div className="col col-lg-7on9 page">
        <p className="clearfix">
          <Link to={backUrl} title="Retour aux offres" className="link-chevron link-chevron--left">
            <span>Retour aux offres</span>
            <i className="icon icon-chevron-left"></i>
          </Link>
        </p>
        <hr className="separator mt-2 mb-2 mb-md-5" />
        <div className="ft-h6 ft-up ft-black-secondary mb-1">{details.structure.name}</div>
        <h2>{details.title}</h2>
        <ul className="list-type ft-h6 ft-up mt-2">
          {details.jobtype && details.jobtype !== '' && (
            <li>
              {' '}
              <i className="icon icon-case is-inline"></i>
              {details.jobtype}
            </li>
          )}
          {details.city && details.city !== '' && (
            <li>
              {' '}
              <i className="icon icon-place is-inline"></i>
              {details.city}
            </li>
          )}
          {details.structure.website && details.structure.website !== '' && (
            <li>
              <i className="icon icon-site is-inline"></i>
              <a
                href={details.structure.website}
                title="Voir le site du club"
                target="_blank"
                rel="noopener noreferrer"
              >
                Voir le site du club
              </a>
            </li>
          )}
          {details.type_contrat && details.type_contrat !== '' && (
            <li>
              {' '}
              <i className="icon icon-jersey is-inline"></i>
              {details.type_contrat}
            </li>
          )}
          {details.date_cloture && details.date_cloture !== '' && (
            <li>
              {' '}
              <i className="icon icon-clock is-inline"></i>
              {details.date_cloture}
            </li>
          )}
        </ul>
        {this.renderDetail()}
        {details.featured_pdf && details.featured_pdf !== '' && (
          <a
            href={details.featured_pdf}
            title="Télécharger l’offre en PDF"
            className="btn btn--white"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="icon icon-download is-inline"></i>
            <span>Télécharger l’offre en PDF</span>
          </a>
        )}
        {form === -1 ? (
          <Loading />
        ) : (
          <JobDetailForm
            form={form}
            jobid={details.id.toString()}
            jobname={`${details.structure.name} - ${details.title}`}
            jobemail={details.email}
          />
        )}
      </div>
    );
  }
}

function renderHelmetDefault(Helmet, pageProps) {
  return (
    <Helmet titleTemplate={`%s`} defaultTitle={pageProps.meta_title || window.location.href}>
      <meta name="keywords" content={`offre emplois ${pageProps.structureName || ''} rugby`} />

      <meta name="description" content={pageProps.meta_description || ''} />
    </Helmet>
  );
}

export default withHelmet(withRouter(JobDetail))(renderHelmetDefault);
